import { Link } from 'react-router-dom';
import {
  ArrowRight, BookOpen, Check, Clock, FileCheck2, GraduationCap,
  Landmark, MapPin, School, ShieldCheck, Sparkles, Undo2, Users,
} from 'lucide-react';
import { CTAPair, FaqAccordion, TestimonialBand, type Testimonial } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /for-institutions — "Director's 8 AM briefing" format.
 *
 * The reader is a director / principal / group admin evaluating
 * Kollez for their whole college or group. This page shows them
 * their Monday morning: a large annotated group roll-up hero, then
 * a timed routine (8:00 → 10:30), then the deliverables (NIRF/NAAC/
 * AICTE), then the concerns (compliance / offer sign-off / Nexie),
 * then the onboarding path.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (for-institutions gets the director's-briefing format).
 */

const CAMPUS_ROLLUP = [
  { name: 'MRIET',  placed: 82, target: 78, drives: 34, trend: '+8',  behind: false },
  { name: 'MRCE',   placed: 74, target: 76, drives: 28, trend: '−2',  behind: true  },
  { name: 'MRCE-A', placed: 71, target: 70, drives: 24, trend: '+1',  behind: false },
  { name: 'MRIT',   placed: 68, target: 72, drives: 22, trend: '−4',  behind: true  },
  { name: 'MRP',    placed: 65, target: 68, drives: 19, trend: '−3',  behind: true  },
];

interface Callout { id: string; label: string; body: string }
const CALLOUTS: Callout[] = [
  { id: 'a', label: 'One number at 8 am', body: 'Group-wide placement rate, refreshed at 05:00 / 08:00 / 12:00 / 18:00. Every college on one row.' },
  { id: 'b', label: 'Behind flag',        body: 'When a college drops below target, we flag it in coral — with a one-click call list of principals to nudge.' },
  { id: 'c', label: 'Drives this season', body: 'Not last quarter. Not annual. Live, per college, per season — with an arrow showing week-over-week direction.' },
  { id: 'd', label: 'Drill to any college', body: 'One tap opens the college view — programs, offers, sign-off queue, Nexie chat, NIRF draft, everything.' },
];

interface RoutineEvent {
  time: string;
  what: string;
  body: string;
  cite: string;
  icon: React.ReactNode;
  tone: 'accent' | 'warning' | 'success';
}

const ROUTINE: RoutineEvent[] = [
  {
    time: '08:00',
    what: 'The roll-up refreshes.',
    body: 'You open Kollez on your phone. Every college in your group shows one row — placed % vs target, drives run, and how the week is trending.',
    cite: 'From Malla Reddy Group · Season 2026-27 · roll-up at 08:00 IST',
    icon: <Clock size={16} />,
    tone: 'accent',
  },
  {
    time: '08:14',
    what: 'Nexie flags 2 colleges behind target.',
    body: 'MRCE is 2 points behind, MRIT is 4 points behind. Nexie proposes a chase list — students with active offers pending acceptance, drives with under-filled slots, partners you haven\'t chased this week.',
    cite: 'Nexie · placement assistant · human-approved before any outreach',
    icon: <Sparkles size={16} />,
    tone: 'warning',
  },
  {
    time: '09:00',
    what: 'You drill into MRCE.',
    body: 'One tap opens the college view. Departments, programs, upcoming drives, offer sign-off queue, Nexie chat, this week\'s NIRF draft — all in one register.',
    cite: 'MRCE college workspace · scoped to your group-admin role',
    icon: <School size={16} />,
    tone: 'accent',
  },
  {
    time: '10:30',
    what: 'You sign off two offers. Ten-minute undo on each.',
    body: 'The offer sign-off queue shows every offer waiting to be released. Automated checks (name match, CTC band, signatory, no double-placement) run before every release. Every acceptance has a 10-minute undo.',
    cite: 'Offer engine · 4 checks · 10-min undo window',
    icon: <ShieldCheck size={16} />,
    tone: 'success',
  },
];

const SIX_JOBS = [
  { job: 'Post a drive',                     time: '90 s' },
  { job: 'Screen 400 applicants',            time: '15 min' },
  { job: 'Schedule 40 interviews',           time: '5 min' },
  { job: 'Release an offer',                 time: '2 min' },
  { job: 'Ask "who\'s placed in ECE?"',     time: '30 s' },
  { job: 'Produce the NIRF report',          time: '1 click' },
];

const INSTITUTION_TESTIMONIAL: Testimonial[] = [
  {
    quote: 'We ran 87 drives last season, up from 34. Every college in the group opens Kollez by 8 am and knows what to chase before their first coffee.',
    name: 'Dr. Ramesh Malla',
    role: 'Director, Placements',
    org: 'Malla Reddy Group',
    number: '87 drives · +156% YoY',
  },
];

/* ────────── sub-components ────────── */

function RollupHero() {
  return (
    <figure
      className="rounded-lg overflow-hidden inkwell relative"
      style={{
        boxShadow: '0 30px 60px -30px rgba(0,0,0,0.35)',
      }}
      aria-label="Group roll-up dashboard at 8 am"
    >
      <div
        className="flex items-baseline justify-between px-6 py-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div>
          <div className="font-serif text-white text-lg leading-tight">Malla Reddy Group · roll-up</div>
          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Today · 08:14 IST · 5 colleges
          </div>
        </div>
        <span
          className="text-xs px-2.5 py-1 rounded-md font-medium"
          style={{ background: 'rgba(232,212,122,0.15)', color: 'rgb(232,212,122)' }}
        >
          Season 2026–27
        </span>
      </div>
      <div
        className="grid px-6 py-3 text-xs uppercase tracking-wider"
        style={{
          color: 'rgba(255,255,255,0.5)',
          gridTemplateColumns: '1fr 78px 78px 78px 60px',
          gap: 12,
        }}
      >
        <span>College</span>
        <span className="text-right">Placed</span>
        <span className="text-right">Target</span>
        <span className="text-right">Trend</span>
        <span className="text-right">Drives</span>
      </div>
      <div className="grid gap-1.5 px-6 pb-4">
        {CAMPUS_ROLLUP.map((c, i) => (
          <div
            key={c.name}
            className="grid items-center px-4 py-3 rounded-md text-white/90"
            style={{
              background: 'rgba(255,255,255,0.04)',
              gridTemplateColumns: '1fr 78px 78px 78px 60px',
              gap: 12,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <span className="flex items-center gap-2 min-w-0 text-sm">
              <School size={14} style={{ color: 'rgba(255,255,255,0.5)' }} />
              <span className="font-medium">{c.name}</span>
              {c.behind && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium"
                  style={{ background: 'rgba(240,138,138,0.18)', color: 'rgb(240,138,138)' }}
                >
                  Behind
                </span>
              )}
              {i === 0 && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium"
                  style={{ background: 'rgba(120,200,140,0.18)', color: 'rgb(160,220,180)' }}
                >
                  Leader
                </span>
              )}
            </span>
            <span className="text-right font-semibold text-sm">{c.placed}%</span>
            <span className="text-right text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>{c.target}%</span>
            <span
              className="text-right text-sm font-medium"
              style={{ color: c.trend.startsWith('+') ? 'rgb(160,220,180)' : 'rgb(240,138,138)' }}
            >
              {c.trend}
            </span>
            <span className="text-right text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{c.drives}</span>
          </div>
        ))}
      </div>
      <figcaption
        className="px-6 py-3 text-xs flex items-center justify-between"
        style={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          color: 'rgba(255,255,255,0.55)',
        }}
      >
        <span>Refreshes at 05:00 / 08:00 / 12:00 / 18:00 IST</span>
        <span>CSV · PDF · NIRF template</span>
      </figcaption>
    </figure>
  );
}

function CalloutList() {
  return (
    <ol className="grid gap-4">
      {CALLOUTS.map((c, i) => (
        <li key={c.id} className="grid gap-2 pl-9 relative">
          <span
            className="absolute left-0 top-0 flex items-center justify-center rounded-full font-serif font-medium"
            style={{
              width: 28, height: 28,
              background: 'rgb(var(--ink-accent))',
              color: 'rgb(var(--ink-on-accent))',
              fontSize: 13,
            }}
          >
            {String.fromCharCode(97 + i).toUpperCase()}
          </span>
          <div className="font-serif text-lg m-0" style={{ letterSpacing: '-0.005em' }}>{c.label}</div>
          <p className="text-sm text-ink-secondary m-0">{c.body}</p>
        </li>
      ))}
    </ol>
  );
}

function RoutineTimeline() {
  return (
    <ol className="grid gap-8 relative">
      {ROUTINE.map((e, i) => (
        <li key={e.time} className="grid grid-cols-[88px_1fr] gap-6 relative">
          <div className="text-right">
            <div
              className="font-serif tabular-nums"
              style={{
                fontSize: '1.625rem',
                fontWeight: 500,
                lineHeight: 1,
                color: 'rgb(var(--ink-primary))',
                letterSpacing: '-0.01em',
              }}
            >
              {e.time}
            </div>
            <div className="text-[10px] uppercase tracking-widest mt-2" style={{ color: 'rgb(var(--ink-tertiary))' }}>
              IST
            </div>
          </div>
          <div className="grid grid-cols-[28px_1fr] gap-4 relative">
            <div className="relative">
              <span
                className="absolute top-1 left-1/2 -translate-x-1/2 flex items-center justify-center rounded-full"
                style={{
                  width: 28, height: 28,
                  background:
                    e.tone === 'warning' ? 'rgb(var(--bg-warning))' :
                    e.tone === 'success' ? 'rgb(var(--bg-success))' :
                                           'rgb(var(--bg-info))',
                  color:
                    e.tone === 'warning' ? 'rgb(var(--ink-warning))' :
                    e.tone === 'success' ? 'rgb(var(--ink-success))' :
                                           'rgb(var(--ink-accent))',
                }}
              >
                {e.icon}
              </span>
              {i < ROUTINE.length - 1 && (
                <span
                  className="absolute left-1/2 -translate-x-1/2 top-9 bottom-[-32px] w-px"
                  style={{ background: 'rgb(var(--line-tertiary))' }}
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="pb-2">
              <h3
                className="font-serif text-xl m-0"
                style={{ lineHeight: 1.2, letterSpacing: '-0.005em' }}
              >
                {e.what}
              </h3>
              <p className="text-ink-secondary m-0 mt-2" style={{ maxWidth: '58ch' }}>{e.body}</p>
              <p
                className="text-xs m-0 mt-2"
                style={{ color: 'rgb(var(--ink-tertiary))', fontStyle: 'italic' }}
              >
                {e.cite}
              </p>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ────────── page ────────── */

export function ForInstitutionsPage() {
  return (
    <>
      <PageMeta
        title="For institutions"
        description="Your Monday morning on Kollez. Group roll-up at 8 AM, Nexie flags 2 colleges behind by 8:14, drill into MRCE at 9:00, sign off two offers by 10:30. A director's briefing, not a features grid."
        canonical="/for-institutions"
      />

      {/* HERO — the roll-up IS the hero */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pt-16 lg:pt-24 pb-8 grid gap-8">
        <div className="max-w-3xl">
          <p className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} />
            For institutions · your Monday at 8 AM
          </p>
          <h1
            className="text-ink-primary mt-2"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            One register. Fifty colleges.<br />
            One number at eight AM.
          </h1>
          <p className="mt-6 text-lg leading-8 text-ink-secondary m-0" style={{ maxWidth: '62ch' }}>
            This page is not a features grid. It&apos;s your Monday morning on
            Kollez — the group roll-up on your phone, what Nexie flags,
            what you drill into, and what you sign off before lunch.
            Every screen is scoped to your group-admin role.
          </p>
          <div className="mt-8">
            <CTAPair
              primary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
              secondary={{ label: 'Start a free pilot', to: '/register/institution?plan=pilot' }}
            />
          </div>
          <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-secondary" aria-label="Trust signals">
            <li className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-ink-success" />SOC 2 · ISO 27001</li>
            <li className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-ink-success" />DPDP Act ready</li>
            <li className="flex items-center gap-1.5"><MapPin size={14} className="text-ink-success" />Data stays in India</li>
          </ul>
        </div>
      </section>

      {/* THE HERO ROLL-UP — full-width dark mock with annotations */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-8 lg:py-12">
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 items-start">
          <div>
            <RollupHero />
          </div>
          <div className="grid gap-5">
            <p className="eyebrow m-0 text-xs">Four things this screen tells you</p>
            <h2
              className="text-ink-primary"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.5rem, 2.75vw, 2rem)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              Everything above, annotated.
            </h2>
            <CalloutList />
          </div>
        </div>
      </section>

      {/* THE MORNING ROUTINE — timed events */}
      <section
        className="border-y border-line-tertiary py-16 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-10">
          <div className="max-w-2xl">
            <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
              <Clock size={12} />
              The morning routine
            </p>
            <h2
              className="text-ink-primary mt-2"
              style={{
                fontFamily: 'Newsreader, Georgia, serif',
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
                margin: 0,
              }}
            >
              08:00 to 10:30. Four moves. One coffee.
            </h2>
          </div>
          <div className="mx-auto max-w-3xl w-full">
            <RoutineTimeline />
          </div>
        </div>
      </section>

      {/* SIX JOBS — deliverables table */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 grid gap-6">
        <div className="max-w-2xl">
          <p className="eyebrow m-0 text-xs">Time-on-task</p>
          <h2
            className="text-ink-primary mt-2"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.15,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              margin: 0,
            }}
          >
            Six placement-cell jobs. Six times.
          </h2>
          <p className="text-ink-secondary mt-3 m-0">
            These are what a TPO office does on a placement day. On Kollez,
            each one takes the time on the right — measured, not estimated.
          </p>
        </div>
        <div className="mt-4 overflow-hidden rounded-lg" style={{ border: '1px solid rgb(var(--line-tertiary))' }}>
          <table className="tbl w-full">
            <caption className="sr-only">Six placement-cell jobs and their time on Kollez</caption>
            <thead>
              <tr>
                <th scope="col" className="text-left">Job</th>
                <th scope="col" className="text-right">Time on Kollez</th>
              </tr>
            </thead>
            <tbody>
              {SIX_JOBS.map((r) => (
                <tr key={r.job}>
                  <td>{r.job}</td>
                  <td className="text-right font-medium tabular-nums">{r.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* REPORTS + NEXIE — two-up, alternating */}
      <section
        className="border-t border-line-tertiary py-16 lg:py-20"
        style={{ background: 'rgb(var(--bg-primary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-12 lg:gap-16">
          {/* NIRF row */}
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1fr] items-center">
            <div className="lg:order-2">
              <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
                <FileCheck2 size={12} />
                Reports · drafted by Nexie
              </p>
              <h2
                className="text-ink-primary mt-2"
                style={{
                  fontFamily: 'Newsreader, Georgia, serif',
                  fontSize: 'clamp(1.5rem, 2.75vw, 2rem)',
                  lineHeight: 1.15,
                  fontWeight: 500,
                  letterSpacing: '-0.005em',
                  margin: 0,
                }}
              >
                NIRF, NAAC, AICTE — Nexie writes the first draft.
              </h2>
              <p className="text-ink-secondary m-0 mt-3" style={{ maxWidth: '52ch' }}>
                Nexie reads your placement records, fills in the format each
                report expects, and cites every number back to the source
                row. Your team edits before it ships — signed export with
                audit trail.
              </p>
              <Link to="/security#reports" className="link mt-4 inline-flex items-center gap-1 text-sm">
                See the audit trail <ArrowRight size={14} />
              </Link>
            </div>
            <div className="lg:order-1">
              <NirfReportMock />
            </div>
          </div>

          {/* Offer sign-off row */}
          <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] items-center pt-12 border-t" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
            <div>
              <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
                <Undo2 size={12} />
                Offer sign-off
              </p>
              <h2
                className="text-ink-primary mt-2"
                style={{
                  fontFamily: 'Newsreader, Georgia, serif',
                  fontSize: 'clamp(1.5rem, 2.75vw, 2rem)',
                  lineHeight: 1.15,
                  fontWeight: 500,
                  letterSpacing: '-0.005em',
                  margin: 0,
                }}
              >
                Every offer, four checks. Every acceptance, ten-minute undo.
              </h2>
              <p className="text-ink-secondary m-0 mt-3" style={{ maxWidth: '52ch' }}>
                Before an offer leaves your placement cell, Kollez checks
                name-vs-roster, CTC-in-band, authorised signatory, and
                no-double-placement. Any check fails, the offer is held
                for human review.
              </p>
            </div>
            <OfferMock />
          </div>

          {/* Nexie assistant row */}
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1fr] items-center pt-12 border-t" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
            <div className="lg:order-2">
              <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
                <Sparkles size={12} />
                Nexie · placement assistant
              </p>
              <h2
                className="text-ink-primary mt-2"
                style={{
                  fontFamily: 'Newsreader, Georgia, serif',
                  fontSize: 'clamp(1.5rem, 2.75vw, 2rem)',
                  lineHeight: 1.15,
                  fontWeight: 500,
                  letterSpacing: '-0.005em',
                  margin: 0,
                }}
              >
                Nexie drafts. You decide.
              </h2>
              <p className="text-ink-secondary m-0 mt-3" style={{ maxWidth: '52ch' }}>
                Ask Nexie any placement question in English or Telugu.
                Every answer cites the records it used — no black-box
                answers, no made-up numbers, no guessing at where a stat
                came from.
              </p>
              <Link to="/ai" className="link mt-4 inline-flex items-center gap-1 text-sm">
                See how Nexie works <ArrowRight size={14} />
              </Link>
            </div>
            <div className="lg:order-1">
              <NexieAnswerMock />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU DELIVER TO EVERY STUDENT */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20">
        <div className="max-w-3xl">
          <p className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} />
            Deliver more to every student · powered by Nexie
          </p>
          <h2 className="h-serif text-3xl lg:text-4xl mt-2">
            The student gets Nexie in their pocket. You get the register.
          </h2>
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <DeliveryTile
            icon={<Sparkles size={18} />}
            title="AI Career Advisor"
            body="Every student gets personal, cited guidance on what to learn and what to apply for — grounded in your college's data."
          />
          <DeliveryTile
            icon={<Users size={18} />}
            title="Resume Builder"
            body="Every student ships a job-ready, ATS-checked resume tailored per role — no more Word templates that recruiters throw out."
          />
          <DeliveryTile
            icon={<GraduationCap size={18} />}
            title="Skill Tracks &amp; Assessments"
            body="Six emerging-skill tracks with portable badges recognised by every partner recruiter on the platform."
          />
        </div>
        <p className="mt-6 text-sm text-ink-tertiary">
          Twelve question types, six assessment modes, four issuer tiers —{' '}
          <Link to="/assessments" className="link">see the full assessments surface</Link>.
        </p>
      </section>

      {/* IMPLEMENTATION + PRICING + TESTIMONIAL — three-up */}
      <section
        className="border-y border-line-tertiary py-16 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-14">
          {/* Timeline */}
          <div>
            <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
              <Clock size={12} />
              Implementation
            </p>
            <h2 className="h-serif text-3xl mt-2 m-0">Live in two weeks.</h2>
            <ol className="mt-6 grid gap-5">
              {[
                { n: 1, title: 'Sign', body: 'MoU + PO. Legal review usually under a week.' },
                { n: 2, title: 'Import roster', body: 'Bulk-upload students by CSV. We map the columns with you.' },
                { n: 3, title: 'Run first drive', body: 'Post it on Kollez, watch the register fill. We stay on-call.' },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[36px_1fr] gap-3 items-baseline">
                  <span
                    className="flex items-center justify-center rounded-full font-semibold"
                    style={{
                      width: 32, height: 32,
                      background: 'rgb(var(--ink-accent))',
                      color: 'rgb(var(--ink-on-accent))',
                      fontSize: 14,
                    }}
                  >
                    {s.n}
                  </span>
                  <div>
                    <div className="font-serif text-lg">{s.title}</div>
                    <p className="helper m-0 mt-1">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Testimonial + pricing callout */}
          <div className="grid gap-5">
            <div className="inkwell rounded-lg p-6">
              <p
                className="font-serif italic m-0"
                style={{
                  fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
                  lineHeight: 1.4,
                  color: 'rgba(255,255,255,0.92)',
                }}
              >
                &ldquo;{INSTITUTION_TESTIMONIAL[0]!.quote}&rdquo;
              </p>
              <div
                className="mt-4 text-sm"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                — {INSTITUTION_TESTIMONIAL[0]!.name}, {INSTITUTION_TESTIMONIAL[0]!.role}, {INSTITUTION_TESTIMONIAL[0]!.org}
              </div>
              <div
                className="mt-2 text-xs font-medium"
                style={{ color: 'rgb(232,212,122)' }}
              >
                {INSTITUTION_TESTIMONIAL[0]!.number}
              </div>
            </div>
            <Link to="/pricing?aud=institutions" className="card card-pad clickable flex items-center gap-4 flex-wrap">
              <span
                className="flex items-center justify-center rounded-lg"
                style={{ width: 44, height: 44, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
              >
                <Landmark size={20} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg leading-tight">Per-college pricing · PO + GST invoice</div>
                <p className="helper m-0 mt-1">Government / aided colleges: automatic discount.</p>
              </div>
              <span className="text-sm text-ink-accent font-medium">See pricing →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* COMPLIANCE STRIP */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-14 lg:py-16">
        <div className="grid gap-4 md:grid-cols-4">
          {[
            [<ShieldCheck size={18} key="1" />, 'SOC 2 Type II'],
            [<ShieldCheck size={18} key="2" />, 'ISO 27001'],
            [<Landmark size={18} key="3" />, 'DPDP Act 2023'],
            [<MapPin size={18} key="4" />, 'Hosted in India'],
          ].map(([icon, title], i) => (
            <div key={i} className="flex items-center gap-3">
              <span style={{ color: 'rgb(var(--ink-success))' }}>{icon}</span>
              <span className="font-medium text-sm">{title}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm">
          <Link className="link" to="/security">See our full security posture →</Link>
        </p>
      </section>

      {/* FAQ */}
      <section
        className="border-t border-line-tertiary py-16 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-3xl px-5 lg:px-10">
          <FaqAccordion
            heading="Common questions"
            emitJsonLd
            items={[
              { q: 'What is the PO process?',
                a: 'Pick a plan, we send a proforma invoice with your GSTIN. Your accounts team raises a PO; we then issue a tax invoice against it. NEFT/RTGS/UPI/card all supported via Razorpay. Legal review is usually under a week.' },
              { q: 'How long is onboarding?',
                a: 'A pilot college is live in 3–4 working days. Group-wide rollout across 10+ colleges takes 2–3 weeks including MoU sign-off. We stay on-call through your first placement drive.' },
              { q: 'What if we leave — do we take our data?',
                a: 'Yes. Full export in NIRF/NAAC/AICTE formats plus a JSON audit log. 60-day soft-delete window before permanent removal. No lock-in clauses.' },
              { q: 'What integrations do you support?',
                a: 'DigiLocker for marks verification, Google/Microsoft SSO for institution staff, WhatsApp Business for student outreach, common ATSes (Darwinbox, Zoho Recruit, Greenhouse) for recruiters, and CSV export from every screen.' },
              { q: 'Do you support NIRF/NAAC/AICTE reporting?',
                a: 'All three. Nexie drafts the report from your own placement records, in the exact format each authority expects — every number cites the source row so your team can verify before signing off. Signed exports with audit trails. See a real report during your demo.' },
              { q: 'How much of the AI is autonomous?',
                a: 'None. Nexie proposes; you dispose. Every report Nexie drafts, every candidate Nexie ranks, every message Nexie writes needs a human on your team to sign off. And every acceptance has a 10-minute undo window. See /ai for the full ethics posture.' },
              { q: 'Can one placement cell manage multiple colleges?',
                a: 'Yes — that is the group roll-up. Post drives once, share applications across colleges you choose, and see a single roll-up dashboard at 8 am with placement targets per college.' },
            ]}
          />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20 text-center">
        <h2 className="display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
          Start with one college this placement season.
        </h2>
        <p className="mt-4 text-ink-secondary max-w-xl mx-auto">
          Free pilot for one college, one season. No card, no PO, no lock-in.
          Upgrade when it earns the upgrade.
        </p>
        <div className="mt-8 flex justify-center">
          <CTAPair
            primary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
            secondary={{ label: 'Start a free pilot', to: '/register/institution?plan=pilot' }}
            align="center"
          />
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="sticky-action">
        <Link to="/contact?topic=demo" className="btn btn-primary btn-lg w-full">
          Book a 30-min demo
        </Link>
      </div>
    </>
  );
}

/* ────────── mocks ────────── */

function NirfReportMock() {
  const rows = [
    { prog: 'B.Tech CSE',  eligible: 320, placed: 302, median: '9.4 LPA', highest: '38 LPA' },
    { prog: 'B.Tech ECE',  eligible: 218, placed: 189, median: '6.8 LPA', highest: '24 LPA' },
    { prog: 'B.Tech ME',   eligible: 176, placed: 121, median: '5.4 LPA', highest: '12 LPA' },
    { prog: 'MCA',         eligible: 88,  placed: 74,  median: '7.2 LPA', highest: '18 LPA' },
    { prog: 'MBA',         eligible: 96,  placed: 82,  median: '8.1 LPA', highest: '22 LPA' },
  ];
  return (
    <figure className="paper card" style={{ maxWidth: 520, width: '100%' }}>
      <div className="p-5">
        <div className="flex items-baseline justify-between gap-3 pb-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <div>
            <div className="font-serif text-xl leading-tight">NIRF · Placement report</div>
            <div className="text-xs" style={{ color: '#5b6070' }}>MRIET · AY 2025–26 · signed export</div>
          </div>
          <span
            className="text-xs px-2 py-0.5 rounded"
            style={{ background: 'rgba(21, 115, 74, 0.12)', color: 'rgb(21, 115, 74)' }}
          >
            Verified
          </span>
        </div>
        <table className="w-full text-sm mt-3" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className="text-left text-[10px] uppercase tracking-wider pb-2" style={{ color: '#5b6070' }}>Program</th>
              <th className="text-right text-[10px] uppercase tracking-wider pb-2" style={{ color: '#5b6070' }}>Eligible</th>
              <th className="text-right text-[10px] uppercase tracking-wider pb-2" style={{ color: '#5b6070' }}>Placed</th>
              <th className="text-right text-[10px] uppercase tracking-wider pb-2" style={{ color: '#5b6070' }}>Median</th>
              <th className="text-right text-[10px] uppercase tracking-wider pb-2" style={{ color: '#5b6070' }}>Highest</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.prog}>
                <td className="py-2 pr-3" style={{ color: '#1a1d2b' }}>{r.prog}</td>
                <td className="py-2 pr-3 text-right tabular-nums" style={{ color: '#1a1d2b' }}>{r.eligible}</td>
                <td className="py-2 pr-3 text-right tabular-nums font-medium" style={{ color: '#1a1d2b' }}>{r.placed}</td>
                <td className="py-2 pr-3 text-right tabular-nums" style={{ color: '#1a1d2b' }}>{r.median}</td>
                <td className="py-2 text-right tabular-nums" style={{ color: '#1a1d2b' }}>{r.highest}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 text-[10px]" style={{ color: '#5b6070' }}>
          Export signed at 2026-05-14 09:22:04 IST. Chain-of-custody: 4 reviewers, 0 revisions.
        </div>
      </div>
    </figure>
  );
}

function OfferMock() {
  const checks = [
    { label: 'Name matches roster', ok: true },
    { label: 'CTC within band (₹4–12 LPA)', ok: true },
    { label: 'Company on approved list', ok: true },
    { label: 'Authorised signatory', ok: true },
    { label: 'Student not placed elsewhere', ok: true },
  ];
  return (
    <figure className="card overflow-hidden" style={{ maxWidth: 460, width: '100%' }}>
      <div className="paper p-5">
        <div className="flex items-baseline justify-between gap-3 pb-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <div>
            <div className="text-xs" style={{ color: '#5b6070' }}>Offer letter · draft</div>
            <div className="font-serif text-lg leading-tight" style={{ color: '#1a1d2b' }}>Freshworks · SDE I</div>
          </div>
          <span
            className="text-xs px-2 py-0.5 rounded"
            style={{ background: 'rgba(21, 115, 74, 0.12)', color: 'rgb(21, 115, 74)' }}
          >
            Ready to release
          </span>
        </div>
        <div className="grid gap-2 mt-3" style={{ color: '#1a1d2b' }}>
          {checks.map((c) => (
            <div key={c.label} className="flex items-center gap-2 text-sm">
              <Check size={16} style={{ color: 'rgb(21, 115, 74)' }} />
              <span>{c.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: '#5b6070' }}>
          <Undo2 size={12} />
          10-minute undo window after student accepts.
        </div>
      </div>
    </figure>
  );
}

function NexieAnswerMock() {
  return (
    <div
      className="card overflow-hidden shadow-md"
      style={{ maxWidth: 480, width: '100%' }}
    >
      <div className="flex items-center gap-2 border-b border-line-tertiary px-4 py-3">
        <span
          className="flex items-center justify-center rounded-md"
          style={{
            width: 24, height: 24,
            background: 'rgb(var(--bg-inkwell))',
            color: 'rgb(var(--ink-inverse))',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          N
        </span>
        <span className="text-sm font-medium">Nexie · placement assistant</span>
      </div>
      <div className="grid gap-3 p-4" style={{ background: 'rgb(var(--bg-secondary))' }}>
        <div className="msg msg-user justify-self-end max-w-[85%]">
          Who&apos;s placed in ECE across the group?
        </div>
        <div className="msg msg-ai max-w-[92%]">
          <p className="m-0">
            189 out of 218 eligible ECE students are placed across your 5 colleges
            (86.7%). MRCE-A is at 92% (highest); MRIT is at 78% (lowest — 12 students
            still open).
          </p>
          <p className="mt-2 helper m-0">
            Cited from: 5 college roll-ups, Season 2026–27 · placement records · roster verified 12 hours ago.
          </p>
          <div className="mt-3 flex gap-2 flex-wrap text-xs">
            <span className="chip">MRCE-A · 92%</span>
            <span className="chip">MRIT · 78%</span>
            <span className="chip">218 records</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeliveryTile({ icon, title, body }: { icon: React.ReactNode; title: React.ReactNode; body: React.ReactNode }) {
  return (
    <div className="card card-pad grid gap-3">
      <span
        className="flex items-center justify-center rounded-lg"
        style={{
          width: 38, height: 38,
          background: 'rgb(var(--bg-info))',
          color: 'rgb(var(--ink-accent))',
        }}
      >
        {icon}
      </span>
      <h3 className="font-serif text-lg">{title}</h3>
      <p className="helper m-0">{body}</p>
    </div>
  );
}
